import type { Revision, RevisionPageDocument, RevisionVariables } from '../../types';
import { isYValue, jsonToY, mutations } from '../../utils';
import { isRevisionPageDocument } from './guards';

type UpdateVariablesOptions = {
    /**
     * If true, all variables will be replaced.
     * @default false
     */
    replace?: boolean;
};

/**
 * Update variables on a revision.
 */
export function updateRevisionVariables(
    revision: mutations.MutableData<Revision>,
    update: RevisionVariables,
    options: UpdateVariablesOptions = {}
) {
    updateVariables(revision, update, options);
}

/**
 * Update variables on a page.
 */
export function updateRevisionPageVariables(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    update: RevisionVariables,
    options: UpdateVariablesOptions = {}
) {
    const page = mutations.getIn(revision, ['pages', pageId] as const);

    if (!page || !isRevisionPageDocument(page)) {
        return;
    }

    updateVariables(page, update, options);
}

function updateVariables(
    obj: mutations.MutableData<Revision | RevisionPageDocument>,
    update: RevisionVariables,
    options: UpdateVariablesOptions = {}
) {
    const { replace = false } = options;

    let variables = mutations.get(obj, 'variables');
    if (!variables) {
        variables = (isYValue(obj) ? jsonToY({}) : {}) as mutations.MutableData<RevisionVariables>;
        mutations.set(obj, 'variables', variables);
    }

    for (const [varName, varValue] of Object.entries(update)) {
        mutations.set(variables, varName, varValue);
    }

    // If replace is true, we need to remove all variables that are not in the update
    if (replace) {
        for (const existingVarName of mutations.keys(variables)) {
            if (update[existingVarName] === undefined) {
                mutations.del(variables, existingVarName);
            }
        }
    }
}
